import React, { FC, Fragment, useEffect, useState } from 'react';
import { Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Order, OrderType, OperationButtonsConfig, InvoicingState } from 'models/order';
import StarRateIcon from '@mui/icons-material/StarRate';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckIcon from '@mui/icons-material/Check';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import CreateWarrantyOrderDialog from 'components/orders/order-details/create-warranty-order-dialog.component';
import CancelByDentistDialog from 'components/orders/order-details/cancel-order-component';
import RateOrderDialog from 'components/orders/order-details/rate-order/rate-order-dialog.component';
import { useNavigate, useLocation } from 'react-router-dom';
import CertifyUncertifyOrderDialog from 'components/orders/order-details/certify-uncertify-order-component';
import ConfirmDialog from 'components/shared/confirm-dialog/confirm-dialog.component';
import { useMutation } from '@apollo/client';
import { certifyUnCertifyOrderTemplate } from 'apollo/templates/order';
import CopyOrderDialog from 'components/orders/order-details/copy-order-dialog.component';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { permissions } from 'utils/permission';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { useOngoingOrderDialog } from 'hooks/useOngoingOrderDialog';
import OngoingOrderDialogContainer from 'components/orders/shared/ongoing-order-dialog/ongoing-order-dialog.container';

type CustomerOperationSectionProps = {
    order: Order;
    showOperationButtons: OperationButtonsConfig | undefined;
    currency: string;
};
const CustomerOperationSection: FC<CustomerOperationSectionProps> = ({
    order,
    showOperationButtons,
    currency,
}: CustomerOperationSectionProps) => {
    const { t: translate } = useTranslation(['orders']);
    const navigate = useNavigate();
    const location = useLocation();
    const currentUser = currentUserVar();
    const currentOrganization = currentOrgVar();
    const [isReadyForInvoice, setIsReadyForInvoice] = useState(false);
    const [isCertified, setIsCertified] = useState(false);
    const [isCertificationEnabled, setIsCertificationEnabled] = useState(false);
    const [showCreateWarrantyOrderButton, setShowCreateWarrantyOrderButton] = useState(false);
    const [warrantyOrderDialogOpen, setWarrantyOrderDialogOpen] = useState(false);
    const [cancelOrderDialog, setCancelOrderDialog] = useState(false);
    const [rateOrderDialogOpen, setRateOrderDialogOpen] = useState(false);
    const [certifiedModel, setCertifiedModel] = useState(false);
    const [conformCertified, setConformCertified] = useState(false);
    const [copyOrderDialogOpen, setCopyOrderDialogOpen] = useState(false);
    const { showBoundary } = useDenthubErrorBoundary();

    const { open, orders, operationType, close, continueOperation, checkOngoingOrder } = useOngoingOrderDialog({
        onOpenWarrantyDialog: () => setWarrantyOrderDialogOpen(true),
        onOpenCopyDialog: () => setCopyOrderDialogOpen(true),
    });

    useEffect(() => {
        const isCertificationEnabled = customerSettings.getCustomerSetting(
            currentOrganization.customerSettings,
            CONSTANTS.SETTINGS_NAMES.CERTIFICATION,
        );
        setIsReadyForInvoice(order.invoicingState === InvoicingState.READY_FOR_INVOICE);
        setIsCertified(order.isCertified);
        setIsCertificationEnabled(isCertificationEnabled);
        if (order.isCreatedByLab) {
            setShowCreateWarrantyOrderButton(
                permissions.isLabTechnician(currentUser.type) &&
                    (order.isProductionFinished || order.invoicingState !== InvoicingState.NOT_READY),
            );
        } else {
            setShowCreateWarrantyOrderButton(
                order.isProductionFinished || order.invoicingState !== InvoicingState.NOT_READY,
            );
        }
    }, [order]);

    // Detect URL hash
    useEffect(() => {
        if (location.hash === '#rate-dialog' && !rateOrderDialogOpen) {
            setRateOrderDialogOpen(true);
        }
    }, [location.hash, rateOrderDialogOpen]);

    const handleCloseDialog = () => {
        setWarrantyOrderDialogOpen(false);
        setCopyOrderDialogOpen(false);
        setCancelOrderDialog(false);
        setRateOrderDialogOpen(false);
        setCertifiedModel(false);
        setConformCertified(false);
        navigate(location.pathname, { replace: true }); // remove #rate-dialog
    };

    const redirectToDraftOrder = (order: Order) => {
        if (order.type === OrderType.SIMPLE_TYPE) {
            return navigate(`/orders/modify-simple/${order._id}`);
        }
        navigate(`/orders/create-action/draft/${order._id}`);
    };
    const [changeStatus] = useMutation(certifyUnCertifyOrderTemplate, {
        onCompleted: () => {
            handleCloseDialog();
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleChangeStatus = async () => {
        await changeStatus({
            variables: {
                orderId: order?._id,
            },
        });
    };

    return (
        <Fragment>
            {isCertificationEnabled && isReadyForInvoice && !isCertified ? (
                <Button
                    onClick={() => {
                        setCertifiedModel(true);
                    }}
                    sx={{ marginLeft: 2 }}
                    startIcon={<CheckIcon />}
                    data-test="dentist-operation-section.certify-order-btn"
                >
                    {translate('orders:CERTIFY')}
                </Button>
            ) : undefined}
            {isCertificationEnabled && isReadyForInvoice && isCertified ? (
                <Button
                    onClick={() => setConformCertified(true)}
                    sx={{ marginLeft: 2 }}
                    startIcon={<CheckCircleIcon />}
                    data-test="dentist-operation-section.certified-btn"
                >
                    {translate('orders:CERTIFIED')}
                </Button>
            ) : undefined}
            {!order.isCreatedByLab && order.type === OrderType.ACTION_BASED ? (
                <Button
                    onClick={() => checkOngoingOrder(order, 'COPY_ORDER')}
                    sx={{ marginLeft: 2 }}
                    startIcon={<ContentCopyIcon />}
                    data-test="dentist-operation-section.copy-order-btn"
                >
                    {translate('orders:COPY_ORDER')}
                </Button>
            ) : undefined}
            {showCreateWarrantyOrderButton ? (
                <Button
                    onClick={() => checkOngoingOrder(order, 'WARRANTY_ORDER')}
                    sx={{ marginLeft: 2 }}
                    startIcon={<VerifiedUserIcon />}
                    data-test="dentist-operation-section.create-warranty-order-btn"
                >
                    {translate('orders:CREATE_WARRANTY_ORDER')}
                </Button>
            ) : undefined}
            {!order.isImportedOrder && showOperationButtons?.rateOrder ? (
                <Button
                    onClick={() => setRateOrderDialogOpen(true)}
                    sx={{ marginLeft: 2 }}
                    startIcon={<StarRateIcon />}
                    data-test="dentist-operation-section.rate-order-btn"
                >
                    {translate('orders:RATE_ORDER')}
                </Button>
            ) : undefined}
            {!order.isImportedOrder && showOperationButtons?.cancelOrder ? (
                <Button
                    onClick={() => setCancelOrderDialog(true)}
                    sx={{ marginLeft: 2 }}
                    startIcon={<CancelIcon />}
                    data-test="dentist-operation-section.cancel-order-btn"
                >
                    {translate('orders:CANCEL_ORDER')}
                </Button>
            ) : undefined}

            <OngoingOrderDialogContainer
                open={open}
                orders={orders}
                operationType={operationType}
                onClose={close}
                onHandleOperation={continueOperation}
            />
            <CreateWarrantyOrderDialog
                open={warrantyOrderDialogOpen}
                onDialogClose={handleCloseDialog}
                onCreateWarrantyOrder={redirectToDraftOrder}
                orderDbId={order._id}
                orderId={order.orderId}
                isCreatedByLab={order.isCreatedByLab || false}
                isDentist={true}
            />
            <CancelByDentistDialog open={cancelOrderDialog} onClose={handleCloseDialog} order={order} />
            <RateOrderDialog open={rateOrderDialogOpen} onClose={handleCloseDialog} order={order} />
            <CertifyUncertifyOrderDialog
                open={certifiedModel}
                onClose={handleCloseDialog}
                order={order}
                currency={currency}
            />
            <ConfirmDialog
                open={conformCertified}
                handleConfirm={handleChangeStatus}
                handleClose={handleCloseDialog}
                dialogTitle={translate('orders:CONFIRM_TITLE')}
                dialogContent={translate('orders:CONFIRM_MESSAGE')}
            />
            <CopyOrderDialog
                open={copyOrderDialogOpen}
                onDialogClose={handleCloseDialog}
                onCreateCopyOrder={redirectToDraftOrder}
                orderDbId={order._id}
                orderId={order.orderId}
            />
        </Fragment>
    );
};
export default CustomerOperationSection;
